import React, { useEffect, useState } from "react";
import { getPublications } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { Calendar, Download, ExternalLink, FileText, Search, Tag } from "lucide-react";

export default function PublicationsPage() {
  const [pubs, setPubs] = useState([]);
  const [search, setSearch] = useState("");
  const [yearFilter, setYearFilter] = useState("all");

  useEffect(() => {
    getPublications().then(r => setPubs(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only fetch, API fn is module-stable

  const years = [...new Set(pubs.map(p => p.year))].sort((a, b) => b - a);
  const filtered = pubs.filter(p => {
    const matchSearch = !search || p.title?.toLowerCase().includes(search.toLowerCase()) || p.authors?.toLowerCase().includes(search.toLowerCase());
    const matchYear = yearFilter === "all" || p.year === parseInt(yearFilter);
    return matchSearch && matchYear;
  });

  return (
    <div data-testid="publications-page" className="bg-[#FAFAFA] min-h-screen">
      <Navbar />
      <div className="bg-gradient-to-br from-[#1670B9] via-[#2d8fd4] to-[#74CDD9] pt-32 pb-20 text-center">
        <h1 className="font-['Quicksand'] text-4xl sm:text-5xl font-light text-white mb-4" data-testid="publications-title">Publications</h1>
        <p className="text-white/80 text-base max-w-xl mx-auto">Publications scientifiques de l'unité de recherche</p>
      </div>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-10">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher par titre ou auteur..." className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1670B9]/20 focus:border-[#1670B9] bg-white" data-testid="pub-search" />
          </div>
          <select value={yearFilter} onChange={e => setYearFilter(e.target.value)} className="px-4 py-3 rounded-xl border border-slate-200 text-sm bg-white" data-testid="pub-year-filter">
            <option value="all">Toutes les années</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20 text-slate-400">
            <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p className="text-xl font-['Quicksand']">{pubs.length === 0 ? "Aucune publication pour le moment" : "Aucun résultat"}</p>
            {pubs.length === 0 && <p className="text-sm mt-2">Les publications seront ajoutées via le panneau d'administration.</p>}
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((pub, i) => (
              <motion.div key={pub.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }}
                className="bg-white rounded-xl border border-slate-100 p-6 hover:shadow-sm transition-shadow" data-testid={`pub-card-${i}`}>
                <div className="flex items-start gap-4">
                  {pub.image_url ? (
                    <img src={pub.image_url} alt="" className="w-16 h-20 rounded-lg object-cover flex-shrink-0 hidden sm:block" />
                  ) : (
                    <div className="w-16 h-20 rounded-lg bg-[#1670B9]/5 flex items-center justify-center flex-shrink-0 hidden sm:block">
                      <FileText className="w-6 h-6 text-[#1670B9]/40" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-['Quicksand'] text-base font-semibold text-slate-800 mb-1">{pub.title}</h3>
                    {pub.authors && <p className="text-sm text-[#1670B9] font-medium mb-1">{pub.authors}</p>}
                    {pub.summary && <p className="text-sm text-slate-500 leading-relaxed mb-2 line-clamp-2">{pub.summary}</p>}
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Calendar className="w-3 h-3" />{pub.year}</span>
                      {pub.doi && <a href={`https://doi.org/${pub.doi}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#1670B9] hover:underline"><ExternalLink className="w-3 h-3" />DOI</a>}
                      {pub.pdf_url && <a href={pub.pdf_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-[#F49C03]/10 text-[#F49C03] text-xs font-medium hover:bg-[#F49C03]/20"><Download className="w-3 h-3" />Télécharger PDF</a>}
                      {pub.tags?.map(t => <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 text-slate-500 text-xs"><Tag className="w-3 h-3" />{t}</span>)}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
